import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import {
  LINK_KIND_APPLICATION,
  LINK_KIND_CREDIT_AUTHORIZATION,
} from "../../hooks/useApplicationLinksHook";

/**
 * The two borrower-facing link kinds, keyed by the `:linkType` route param.
 * `publicPath` is the form the minted URL lands on — shown while the link is
 * being composed so the broker knows what the borrower will see.
 *
 * `borrowerFields` / `loanFields` are the prefill inputs offered for that kind.
 *
 * THE RULE: a field belongs in these lists only if it is BOTH (a) rendered by
 * that specific public form under exactly this name, and (b) in the server's
 * PREFILL_ALLOWED. Failing (a) makes the input inert — the value is stored on
 * the link and lands on a field that does not exist. Failing (b) makes it inert
 * too — the server drops it silently and reports it as a dropped field.
 *
 * The two forms genuinely differ, so these lists are NOT interchangeable:
 *   - the application asks entityName + estimatedFICO; the credit auth does not
 *   - the credit auth asks middleName + nameSuffix; the application does not
 *   - primaryAddress is no longer offered on either. The borrower types their
 *     own address: the server refuses it on a credit-auth link outright (name
 *     plus address is most of an identity-theft payload), and prefilling it on
 *     the application alone made the same typed value reach one link and not
 *     the other. It stays in the client allowlist because that mirrors what the
 *     SERVER accepts, not what this screen offers.
 *
 * Verified against `grep -rho 'name="..."'` over each form's folder. Re-run that
 * before adding an entry; do not infer the list from the allowlist.
 */
export const SEND_LINK_CONFIGS = {
  "loan-application": {
    linkKind: LINK_KIND_APPLICATION,
    publicPath: "/loan-application",
    shortName: "loan application",
    // Title-case and abbreviated for the header buttons, where the full
    // sentence-case name makes the pair too wide to sit beside the title.
    blankLabel: "Loan App",
    // Result-card identity: sentence-cased name, one-line description of what
    // travelled, and the icon tile's tint pair from theme/colors `status`.
    successNoun: "Loan application",
    resultSubtitle: "Prefilled with borrower, loan and property details",
    icon: DescriptionOutlinedIcon,
    tone: "info",
    title: "Send a loan application",
    description:
      "Generate a unique link to the full loan application. Anything you prefill below is already filled in when the borrower opens it.",
    submissionsPath: "/submissions/applications",
    submissionsLabel: "View submitted applications",
    successTitle: "Application link ready",
    emailSubject: "Your loan application",
    emailIntro:
      "Please use the secure link below to complete your loan application:",
    borrowerFields: [
      "firstName",
      "lastName",
      "phone",
      "citizenshipStatus",
      "entityName",
      "estimatedFICO",
    ],
    loanFields: [
      "loanProductType",
      "loanPurpose",
      "baseLoanAmount",
      "expectedClosingDate",
      "subjectPropertyAddress",
    ],
  },
  "credit-auth": {
    linkKind: LINK_KIND_CREDIT_AUTHORIZATION,
    publicPath: "/credit-auth",
    shortName: "credit authorization",
    blankLabel: "Credit Auth",
    successNoun: "Credit authorization",
    resultSubtitle: "Prefilled with the fields its form accepts",
    icon: VerifiedUserOutlinedIcon,
    tone: "success",
    title: "Send a credit authorization",
    description:
      "Generate a unique link that asks the borrower to e-sign consent for a credit pull. Prefill what you already know to keep it short.",
    submissionsPath: "/submissions/credit-auths",
    submissionsLabel: "View submitted authorizations",
    successTitle: "Credit authorization link ready",
    emailSubject: "Your credit authorization",
    emailIntro:
      "Please use the secure link below to review and sign your credit authorization:",
    borrowerFields: [
      "firstName",
      "middleName",
      "lastName",
      "nameSuffix",
      "phone",
      "citizenshipStatus",
    ],
    loanFields: [],
  },
};

export const LINK_SELECTION_BOTH = "both";

export const LINK_SELECTION_OPTIONS = [
  {
    value: "loan-application",
    label: "Loan application",
    description: "Full application, tailored by product & purpose",
  },
  {
    value: "credit-auth",
    label: "Credit authorization",
    description: "Consent to pull the borrower's credit",
  },
  {
    value: LINK_SELECTION_BOTH,
    label: "Both",
    // Worth stating: one send, not two, and the order is fixed.
    description: "One email, two links — application first",
  },
];

export const DELIVERY_EMAIL = "email";
export const DELIVERY_SHAREABLE = "shareable";

/**
 * How the borrower gets the link. This is NOT a cosmetic choice — the two modes
 * hit different endpoints and produce genuinely different links:
 *
 *   email     -> POST links/send      targeted, one borrower, PREFILLED,
 *                                     30-day expiry, raw URL shown once.
 *   shareable -> POST links/dedicated evergreen, reusable by anyone who gets it,
 *                                     NO prefill, never expires, same URL forever.
 *
 * Attribution is the one thing they share: both stamp `owner_user_id` from the
 * caller's session, so a submission through either routes back to the LO. That
 * is the whole point of using a generated link over the blank public form.
 *
 * The no-prefill part is why picking "shareable" hides the prefill sections
 * rather than greying them out — a reusable link has no single borrower to
 * prefill it for, and the server stores `prefill: {}` regardless of what is sent.
 */
export const DELIVERY_OPTIONS = [
  {
    value: DELIVERY_EMAIL,
    label: "Email it to the borrower",
    // Names prefill, which is otherwise invisible at the point of choosing:
    // the prefill sections only appear AFTER an email address is entered, so
    // an LO comparing the two cards would have no way to know what taking the
    // link instead costs them. Kept to two lines at the card's one-third
    // width, as is the option beside it.
    description: "The link is emailed to them, prefilled with details you enter.",
    icon: MailOutlineRoundedIcon,
  },
  {
    value: DELIVERY_SHAREABLE,
    label: "Get a shareable link",
    // Names a signature specifically: it is the case the permanent, reusable
    // link exists for, and the one an LO is least likely to guess is allowed.
    description: "Copy the link and paste it anywhere, like your Outlook signature.",
    icon: LinkRoundedIcon,
  },
];

/**
 * The configs a selection resolves to, in the order their links are minted and
 * displayed. "Both" leads with the application because it is the longer form
 * and the one a broker is usually chasing.
 */
export const configsForSelection = (selection) =>
  selection === LINK_SELECTION_BOTH
    ? [SEND_LINK_CONFIGS["loan-application"], SEND_LINK_CONFIGS["credit-auth"]]
    : [SEND_LINK_CONFIGS[selection]].filter(Boolean);

/**
 * Display order for the merged prefill list. This ONLY decides sequence — a
 * field still has to be offered by one of the selected configs to appear at
 * all, so the per-form rules documented above still hold and this list can't
 * smuggle a field onto a form that doesn't have it.
 *
 * Needed because the two lists interleave: concatenating them would strand
 * middleName and nameSuffix (credit-auth only) after the application-only
 * fields rather than beside the other name fields.
 */
const MERGED_FIELD_ORDER = [
  "firstName",
  "middleName",
  "lastName",
  "nameSuffix",
  "emailAddress",
  "phone",
  "citizenshipStatus",
  "entityName",
  "estimatedFICO",
  "loanProductType",
  "loanPurpose",
  "baseLoanAmount",
  "expectedClosingDate",
  "subjectPropertyAddress",
];

/** Deduped union of `key` across configs, in MERGED_FIELD_ORDER sequence. */
export const mergedFields = (configs, key) => {
  const fields = [...new Set(configs.flatMap((config) => config[key]))];
  return [
    ...MERGED_FIELD_ORDER.filter((name) => fields.includes(name)),
    // Anything not yet in the order list still renders, just at the end.
    ...fields.filter((name) => !MERGED_FIELD_ORDER.includes(name)),
  ];
};

/**
 * Human labels for the prefill inputs, for messages that name a field back to
 * the broker. Kept beside the field lists rather than read off the registry —
 * the registry's labels live inside JSX and aren't addressable.
 */
export const PREFILL_FIELD_LABELS = {
  firstName: "First Name",
  middleName: "Middle Name",
  lastName: "Last Name",
  nameSuffix: "Suffix",
  emailAddress: "Email",
  phone: "Phone",
  entityName: "Entity Name",
  citizenshipStatus: "Citizenship",
  estimatedFICO: "Estimated FICO range",
  primaryAddress: "Primary Address",
  loanProductType: "Product type",
  loanPurpose: "Loan purpose",
  baseLoanAmount: "Loan amount",
  expectedClosingDate: "Expected closing date",
  subjectPropertyAddress: "Subject property address",
};

/**
 * Route-config for a server `link_kind`. The mint response identifies each link
 * by kind, not by the route param this screen is keyed on, so the results have
 * to be mapped back.
 */
export const configForLinkKind = (linkKind) =>
  Object.values(SEND_LINK_CONFIGS).find(
    (config) => config.linkKind === linkKind,
  ) || null;

export const LINK_EXPIRY_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 0, label: "No expiration" },
];
